import { UserAvatarFilled } from "@carbon/icons-react";
import CheckIcon from "@mui/icons-material/Check";
import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useUserContext } from "../../contexts/UserContext";
import { UserActionTypes } from "../../contexts/reducers/userReducer";
import useSetActiveRoleMutation from "../../hooks/graphql-hooks/mutations/useSetActiveRoleMutation";
import useRolesQuery from "../../hooks/graphql-hooks/queries/useRolesQuery";
import { Roles } from "../../models/autogenerated-deliveryService-gql/graphql";
import { RoutePath } from "../../models/routePath";
import {
  NormalizedRoles,
  normalizeRolesOutput,
} from "../../utils/roleNormalization";

interface NavbarUserDropdownProps {
  authorized: boolean;
}

function NavbarUserDropdown({ authorized }: NavbarUserDropdownProps) {
  const { t } = useTranslation();
  const { dispatchUser } = useUserContext();
  const navigate = useNavigate();
  const [anchorMenu, setAnchorMenu] = React.useState<any>(null);
  const [roles, setRoles] = React.useState<NormalizedRoles>({
    activeRole: null,
    assignedRoles: [],
  });

  const rolesQuery = useRolesQuery({
    skip: !authorized,
    dataAction: (data) => {
      setRoles(normalizeRolesOutput(data));
    },
  });

  const [setActiveRole] = useSetActiveRoleMutation();

  const toggleMenu = (event: React.SyntheticEvent) => {
    setAnchorMenu(event.currentTarget);
    rolesQuery.refetch();
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  const handleSignOut = () => {
    dispatchUser({ type: UserActionTypes.Logout });
  };

  const getRoleLabel = (role: Roles) => {
    switch (role) {
      case Roles.Supplier:
        return t("supplier");
      case Roles.Carrier:
        return t("carrier");
      case Roles.Customer:
        return t("customer");
      default:
        return role;
    }
  };

  const handleRoleChange = async (role: Roles) => {
    if (role === roles.activeRole) {
      closeMenu();
      return;
    }

    await setActiveRole({ variables: { role } });

    setRoles((prev) => ({ ...prev, activeRole: role }));
    dispatchUser({ type: UserActionTypes.SetRole, value: role });
    navigate(RoutePath.Home, { state: { refreshAt: Date.now() } });
    closeMenu();
  };

  const isSwitchingRole = rolesQuery.loading;

  return (
    <React.Fragment>
      <Tooltip title="Account">
        <IconButton
          aria-owns={Boolean(anchorMenu) ? "menu-appbar" : undefined}
          aria-haspopup="true"
          onClick={toggleMenu}
          color="inherit"
          size="large"
          className="user-icon"
        >
          <UserAvatarFilled />
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
      >
        {roles.assignedRoles.map((role) => (
          <MenuItem
            key={role}
            disabled={isSwitchingRole}
            onClick={() => handleRoleChange(role)}
            selected={roles.activeRole === role}
          >
            {getRoleLabel(role)}
            {roles.activeRole === role && (
              <ListItemIcon sx={{ minWidth: 0, marginLeft: "auto" }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleSignOut}>{t("signOut")}</MenuItem>
      </Menu>
    </React.Fragment>
  );
}

export default NavbarUserDropdown;
